/**
 * API Configuration
 *
 * This file contains all API-related configuration including base URLs, endpoints, and ports.
 * Update this file to change API settings across the entire application.
 *
 * ⚠️ IMPORTANT: Change these values to match your backend server configuration
 */

// ============================================================================
// API BASE CONFIGURATION - CHANGE THESE VALUES
// ============================================================================

/**
 * API Host - Change this to your backend server hostname
 * Examples:
 * - Development: 'localhost'
 * - Production: 'api.yourdomain.com'
 * - Docker: 'backend' (service name)
 */
export const API_HOST = 'expence.parseronline.com';

/**
 * API Port - Change this to your backend server port
 * Examples:
 * - Development: 3001, 3002, 8080
 * - Production: 443 (HTTPS), 80 (HTTP)
 */
export const API_PORT = '8080';

/**
 * API Protocol - http or https
 */
export const API_PROTOCOL = 'https';

/**
 * API Base Path - The base path for all API endpoints
 * Usually '/api' or empty string
 */
export const API_BASE_PATH = '/api';

// ============================================================================
// COMPUTED API URLS - DO NOT MODIFY
// ============================================================================

/**
 * Full API Base URL
 * Automatically constructed from the above configuration
 */
export const API_BASE_URL = `${API_PROTOCOL}://${API_HOST}${API_BASE_PATH}`;

/**
 * API Base URL without /api suffix (for direct endpoint access)
 */
export const API_ROOT_URL = `${API_PROTOCOL}://${API_HOST}`;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get family_id from localStorage
 * @returns Family ID string
 * @throws Error if family data is not found or invalid
 */
export const getFamilyId = (): string => {
  const familyData = localStorage.getItem('family');
  if (!familyData) {
    throw new Error('Family data not found. Please login again.');
  }
  try {
    const family = JSON.parse(familyData);
    if (!family.id) {
      throw new Error('Family ID not found. Please login again.');
    }
    return family.id;
  } catch (error) {
    throw new Error('Invalid family data. Please login again.');
  }
};

// ============================================================================
// API ENDPOINTS
// ============================================================================

/**
 * Authentication Endpoints
 */
export const AUTH_ENDPOINTS = {
  LOGIN: `${API_BASE_URL}/auth/login`,
  REGISTER_FAMILY: `${API_BASE_URL}/auth/register-family`,
  REGISTER: `${API_BASE_URL}/auth/register`,
  SET_PASSWORD: `${API_BASE_URL}/auth/set-password`,
  VERIFY_TOKEN: (token: string) => `${API_BASE_URL}/auth/verify-token/${token}`,
  LOGOUT: `${API_BASE_URL}/auth/logout`,
  REFRESH_TOKEN: `${API_BASE_URL}/auth/refresh`,
} as const;

/**
 * User Endpoints (Family-scoped)
 * Note: These functions dynamically get family_id from localStorage
 */
export const USER_ENDPOINTS = {
  GET_ALL: () => {
    const familyId = getFamilyId();
    return `${API_BASE_URL}/families/${familyId}/users`;
  },
  CREATE: () => {
    const familyId = getFamilyId();
    return `${API_BASE_URL}/families/${familyId}/users`;
  },
  GET_BY_ID: (id: string) => {
    const familyId = getFamilyId();
    return `${API_BASE_URL}/families/${familyId}/users/${id}`;
  },
  UPDATE: (id: string) => {
    const familyId = getFamilyId();
    return `${API_BASE_URL}/families/${familyId}/users/${id}`;
  },
  DELETE: (id: string) => {
    const familyId = getFamilyId();
    return `${API_BASE_URL}/families/${familyId}/users/${id}`;
  },
  GET_EXPENSES: (id: string) => {
    const familyId = getFamilyId();
    return `${API_BASE_URL}/families/${familyId}/users/${id}/expenses`;
  },
  GET_INCOMES: (id: string) => {
    const familyId = getFamilyId();
    return `${API_BASE_URL}/families/${familyId}/users/${id}/incomes`;
  },
};

/**
 * Expense Endpoints (Family-scoped)
 * Note: These functions dynamically get family_id from localStorage
 */
export const EXPENSE_ENDPOINTS = {
  GET_ALL: () => {
    const familyId = getFamilyId();
    return `${API_BASE_URL}/families/${familyId}/expenses`;
  },
  CREATE: () => {
    const familyId = getFamilyId();
    return `${API_BASE_URL}/families/${familyId}/expenses`;
  },
  GET_BY_ID: (id: string) => {
    const familyId = getFamilyId();
    return `${API_BASE_URL}/families/${familyId}/expenses/${id}`;
  },
  UPDATE: (id: string) => {
    const familyId = getFamilyId();
    return `${API_BASE_URL}/families/${familyId}/expenses/${id}`;
  },
  DELETE: (id: string) => {
    const familyId = getFamilyId();
    return `${API_BASE_URL}/families/${familyId}/expenses/${id}`;
  },
  ANALYTICS: () => {
    const familyId = getFamilyId();
    return `${API_BASE_URL}/families/${familyId}/analytics/expenses`;
  },
};

/**
 * Income Endpoints (Family-scoped)
 * Note: These functions dynamically get family_id from localStorage
 */
export const INCOME_ENDPOINTS = {
  GET_ALL: () => {
    const familyId = getFamilyId();
    return `${API_BASE_URL}/families/${familyId}/incomes`;
  },
  CREATE: () => {
    const familyId = getFamilyId();
    return `${API_BASE_URL}/families/${familyId}/incomes`;
  },
  GET_BY_ID: (id: string) => {
    const familyId = getFamilyId();
    return `${API_BASE_URL}/families/${familyId}/incomes/${id}`;
  },
  UPDATE: (id: string) => {
    const familyId = getFamilyId();
    return `${API_BASE_URL}/families/${familyId}/incomes/${id}`;
  },
  DELETE: (id: string) => {
    const familyId = getFamilyId();
    return `${API_BASE_URL}/families/${familyId}/incomes/${id}`;
  },
  GET_BY_USER: (userId: string) => {
    const familyId = getFamilyId();
    return `${API_BASE_URL}/families/${familyId}/users/${userId}/incomes`;
  },
  ANALYTICS: () => {
    const familyId = getFamilyId();
    return `${API_BASE_URL}/families/${familyId}/analytics/incomes`;
  },
  FINANCIAL_SUMMARY: () => {
    const familyId = getFamilyId();
    return `${API_BASE_URL}/families/${familyId}/analytics/financial-summary`;
  },
};

/**
 * Family Endpoints (Family-scoped)
 * Note: These functions dynamically get family_id from localStorage
 */
export const FAMILY_ENDPOINTS = {
  GET_CURRENT: () => {
    const familyId = getFamilyId();
    return `${API_BASE_URL}/families/${familyId}`;
  },
  UPDATE: (id?: string) => {
    const familyId = id || getFamilyId();
    return `${API_BASE_URL}/families/${familyId}`;
  },
  GET_BY_ID: (id?: string) => {
    const familyId = id || getFamilyId();
    return `${API_BASE_URL}/families/${familyId}`;
  },
};

// ============================================================================
// API CONFIGURATION OBJECT
// ============================================================================

/**
 * Complete API Configuration
 * Export this for easy access to all API settings
 */
export const API_CONFIG = {
  host: API_HOST,
  port: API_PORT,
  protocol: API_PROTOCOL,
  basePath: API_BASE_PATH,
  baseUrl: API_BASE_URL,
  rootUrl: API_ROOT_URL,
  endpoints: {
    auth: AUTH_ENDPOINTS,
    users: USER_ENDPOINTS,
    expenses: EXPENSE_ENDPOINTS,
    incomes: INCOME_ENDPOINTS,
    families: FAMILY_ENDPOINTS,
  },
} as const;

/**
 * Build a full URL from a path
 * @param path - The API path (e.g., '/users/123')
 * @returns Full URL
 */
export const buildApiUrl = (path: string): string => {
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${API_ROOT_URL}${cleanPath}`;
};

/**
 * Build a family-scoped URL
 * @param path - The API path after /families/{family_id}/ (e.g., 'users', 'expenses/123')
 * @returns Full URL with family_id
 */
export const buildFamilyUrl = (path: string): string => {
  const familyId = getFamilyId();
  const cleanPath = path.startsWith('/') ? path.substring(1) : path;
  return `${API_BASE_URL}/families/${familyId}/${cleanPath}`;
};

/**
 * Log current API configuration (useful for debugging)
 */
export const logApiConfig = (): void => {
  console.log('🔧 API Configuration:', {
    'Base URL': API_BASE_URL,
    'Host': API_HOST,
    'Port': API_PORT,
    'Protocol': API_PROTOCOL,
  });
};

export default API_CONFIG;

